<template>
  <div class="general-container">
    <h1>Proyectos y Trayectoria</h1>
    <p class="intro-texto">
      Colaboramos con ayuntamientos, centros educativos y festivales, y desarrollamos líneas propias de divulgación científica y ambiental. Detrás de cada proyecto hay una base sólida en ciencia, educación y tecnología.
    </p>

    <!-- CIFRAS -->
    <div class="cifras">
      <div class="cifra" v-for="cifra in cifras" :key="cifra.texto">
        <strong>{{ cifra.valor }}</strong>
        <span>{{ cifra.texto }}</span>
      </div>
    </div>

    <!-- PROYECTOS DESTACADOS -->
    <section class="seccion">
      <h2>Proyectos destacados</h2>
      <div class="proyectos-grid">
        <div class="card" v-for="item in proyectos" :key="item.title" @click="goToDetail(item.link)">
          <img :src="item.image" :alt="item.title + ' — imagen del proyecto'" :title="item.title" loading="lazy">
          <div class="etiquetas">
            <span class="etiqueta" v-for="linea in item.lineas" :key="linea">{{ linea }}</span>
          </div>
          <h3>{{ item.title }}</h3>
          <p class="cliente">{{ item.cliente }}</p>
          <p>{{ item.description }}</p>
        </div>
      </div>
    </section>

    <!-- COLABORACIONES -->
    <section class="seccion">
      <h2>Han confiado en nosotros</h2>
      <div class="colaboraciones-grid">
        <div class="colaboracion" v-for="colab in colaboraciones" :key="colab.entidad">
          <img :src="colab.image" :alt="colab.alt" :title="colab.entidad" loading="lazy">
          <div>
            <h3>{{ colab.entidad }}</h3>
            <p>{{ colab.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- QUIÉN HAY DETRÁS -->
    <section class="seccion">
      <h2>Quién hay detrás</h2>
      <div class="persona">
        <img class="persona-foto" src="/imagenes/Aintzane.avif" alt="Aintzane Goffard, fundadora de Geobizi" title="Aintzane Goffard" loading="lazy">
        <div>
          <p>
            Geobizi nace en 2019 de la mano de <strong>Aintzane Goffard</strong>: geóloga, docente y desarrolladora web. Esa mezcla es la que nos permite diseñar una ruta con rigor científico, adaptarla a cualquier grupo con criterios pedagógicos y, si hace falta, construir la herramienta digital que la acompaña.
          </p>
          <a href="https://aintzane-goffard.com/" target="_blank" rel="noopener" class="btn-secundario">Ver trayectoria completa</a>
        </div>
      </div>

      <div class="pilares-grid">
        <div class="pilar" v-for="pilar in pilares" :key="pilar.title">
          <h3>{{ pilar.icon }} {{ pilar.title }}</h3>
          <ul>
            <li v-for="punto in pilar.puntos" :key="punto">{{ punto }}</li>
          </ul>
        </div>
      </div>

      <div class="publicaciones">
        <h3>Publicaciones científicas</h3>
        <ul>
          <li v-for="pub in publicaciones" :key="pub.title">
            <a :href="pub.link" target="_blank" rel="noopener">{{ pub.title }}</a>
            <span class="pub-fuente">{{ pub.fuente }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- GEOBIZI EN LOS MEDIOS -->
    <section class="seccion">
      <h2>Geobizi en los medios</h2>
      <div class="medios-grid">
        <div class="medio-card">
          <h3>📻 Entrevista en Radio</h3>
          <p>Conversamos sobre la historia geológica de nuestro planeta desde el punto de vista de nuestra ruta en el Flysch de Bizkaia en Getxo.</p>
          <a href="https://cadenaser.com/emisora/2020/08/28/radio_san_sebastian/1598613055_684629.html" target="_blank" rel="noopener" class="btn-secundario">Escuchar entrevista</a>
        </div>

        <div class="medio-card">
          <h3>📰 El Correo</h3>
          <p>Aparición en prensa escrita destacando iniciativas locales de sensibilización, conocimiento, protección del entorno y nuestras actividades.</p>
          <a href="https://www.elcorreo.com/planes/playa-descubrir-secretos-arenales-populares-20260807185045-nt.html" target="_blank" rel="noopener" class="btn-secundario">Leer noticia</a>
        </div>

        <div class="medio-card">
          <h3>📺 Minirreportaje en ETB</h3>
          <p>Reportaje en televisión mostrando sobre el terreno una de nuestras rutas geológicas sobre el Flysch de Bizkaia en Sopela.</p>
          <a href="https://www.eitb.eus/eu/telebista/programak/sukalerrian/bideoak/osoa/8955046/bideoa-sopelako-flyscharen-ingurua-ezagutu-dugu-geotxiki-enpresako-aintzaneren-eskutik/" target="_blank" rel="noopener" class="btn-secundario">Ver reportaje</a>
        </div>
      </div>
    </section>

    <!-- CIERRE -->
    <section class="cierre">
      <h2>¿Tienes un proyecto en mente?</h2>
      <p>Cuéntanos qué necesitas y lo diseñamos contigo.</p>
      <router-link to="/contacto" class="btn-principal">Hablemos</router-link>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'

const router = useRouter()

const pageUrl = 'https://www.geobizi.com/proyectos'
const ogImage = 'https://www.geobizi.com/imagenes/og-geobizi.jpg'

useHead({
  title: 'Proyectos y Trayectoria | Geobizi',
  meta: [
    { name: 'description', content: 'Proyectos de Geobizi con ayuntamientos, festivales y líneas propias de divulgación, y la trayectoria científica, docente y técnica que los respalda.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'theme-color', content: '#0b8a4c' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Proyectos y Trayectoria | Geobizi' },
    { property: 'og:description', content: 'Colaboraciones con instituciones, festivales y proyectos de educación ambiental realizados por Geobizi.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage },
    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' }
  ]
})

const cifras = [
  { valor: '+7', texto: 'años de actividad' },
  { valor: '+90', texto: 'actividades abiertas en los últimos 2 años' },
  { valor: '+10', texto: 'municipios' },
  { valor: '+15', texto: 'centros escolares' },
]

// Etiquetas alineadas con las líneas de servicio: Didáctica, Biorregeneración, Digital
const proyectos = ref([
  {
    title: 'Zalla Natura',
    cliente: 'Ayuntamiento de Zalla',
    lineas: ['Biorregeneración', 'Didáctica'],
    description: 'Biorregeneración colaborativa durante todo el año: talleres, rutas y actividades con el vecindario, los centros escolares y la residencia.',
    link: '/detalle-zalla-natura',
    image: '/imagenes/proyectos/zallanatura/zallanatura.avif',
  },
  {
    title: 'Semana Verde (Aste Berdea)',
    cliente: 'Getxo · cada año',
    lineas: ['Didáctica'],
    description: 'Talleres medioambientales, rutas geológicas, hoteles de insectos y limpiezas de playa.',
    link: '/detalle-aste-berdea',
    image: '/imagenes/proyectos/asteberdea/semanaverde.avif',
  },
  {
    title: 'Sopela Kosta Fest',
    cliente: 'Ayuntamiento de Sopela · cada año',
    lineas: ['Didáctica', 'Digital'],
    description: 'Talleres, rutas de fósiles, yincana digital y actividades familiares junto al mar.',
    link: '/detalle-sopela-kosta-fest',
    image: '/imagenes/proyectos/sopelakostafest/sopelakostafest.avif',
  },
  {
    title: 'Flysch de Bizkaia',
    cliente: 'Proyecto propio',
    lineas: ['Didáctica', 'Digital'],
    description: 'Rutas guiadas, yincanas digitales interactivas y material didáctico abierto sobre el Flysch.',
    link: '/detalle-flysch',
    image: '/imagenes/proyectos/flyschdigital/flyschdigital.avif',
  }
])

const colaboraciones = [
  {
    entidad: 'Getxo',
    texto: 'Programa de rutas geológicas y ambientales y talleres con fechas garantizadas, acordado con el Ayuntamiento durante años. Además, talleres cada año en Aste Berdea (Semana Verde), Asteklima (Semana del Clima y la Energía) y las Jornadas Europeas de Patrimonio.',
    image: '/imagenes/proyectos/colaboraciones/getxo-azterkosta.avif',
    alt: 'Participante sonriente muestra tubos de ensayo con muestras de agua en Azterkosta, Getxo',
  },
  {
    entidad: 'Ayuntamiento de Mungia',
    texto: 'Con el área de Medio Ambiente: talleres de biodiversidad y deforestación para los centros escolares, actividades en Asteklima y talleres en el Mercado de Flores.',
    image: '/imagenes/servicios/actividades/actividades.avif',
    alt: 'Taller de pintar eguzkilores en Mungia',
  },
  {
    entidad: 'Ayuntamiento de Sopela',
    texto: 'Excursiones geológicas propias y, cada año, rutas y talleres en el Sopela Kosta Fest, incluida la yincana Flysch Digital en la playa.',
    image: '/imagenes/proyectos/colaboraciones/sopela-cianotipias.avif',
    alt: 'Cianotipias azules con siluetas de plantas e insectos secándose en un tendedero',
  },
  {
    entidad: 'Ayuntamiento de Zalla',
    texto: 'Zalla Natura: biorregeneración colaborativa durante todo el año con familias, centros escolares y la residencia.',
    image: '/imagenes/proyectos/colaboraciones/zalla-hotel-insectos.avif',
    alt: 'Una familia monta junta un hotel de insectos con cañas y tacos de madera',
  },
  {
    entidad: 'Red de Ekoetxeas',
    texto: 'Análisis de agua, herbarios pintados, suelos y nendo dango, juguetes sostenibles y cosmética natural sostenible: jabones de glicerina, ambientadores de cera y aceite de masaje.',
    image: '/imagenes/proyectos/colaboraciones/ekoetxea-agua.avif',
    alt: 'Niños y niñas con guantes analizan una muestra de agua en un taller de Ekoetxea',
  },
  {
    entidad: 'Trapagaran',
    texto: 'Taller de arte prehistórico: pinturas hechas con óxidos de hierro y huevo para pintar bisontes, ciervos y caballos.',
    image: '/imagenes/proyectos/colaboraciones/trapagaran-rupestre.avif',
    alt: 'Cuencos con pigmentos de óxido de hierro y láminas con un mamut y bisontes pintados',
  },
  {
    entidad: 'Ayuntamiento de Güeñes',
    texto: 'Bombas de semillas y eguzkilores pintados en la Feria de San José.',
    image: '/imagenes/proyectos/colaboraciones/guenes-eguzkilore.avif',
    alt: 'Eguzkilore pintado en rojo y amarillo sobre un disco de madera',
  },
  {
    entidad: 'Centros escolares',
    texto: 'Rutas geológicas y ambientales en Getxo y Sopela, talleres de biodiversidad, prensado de flores, casas de insectos y bombas de semillas, y formación STEAM para profesorado.',
    image: '/imagenes/proyectos/colaboraciones/centros-escolares.avif',
    alt: 'Una niña recoge una muestra de agua de un charco con guantes',
  },
]

const pilares = [
  {
    icon: '🪨',
    title: 'Ciencia',
    puntos: [
      'Licenciatura en Geología (UPV/EHU)',
      'Máster en Cuaternario: Cambios Ambientales y Huella Humana (UPV/EHU)',
      'Investigación en micropaleontología y evolución ambiental costera en la UPV/EHU',
    ]
  },
  {
    icon: '🎓',
    title: 'Docencia',
    puntos: [
      'Máster en Formación del Profesorado (VIU)',
      'Máster en Neuropsicología y Educación (UNIR, en curso)',
      'Docente de Formación Profesional para el Empleo y teleformación acreditada por Lanbide',
      'Diseño Universal para el Aprendizaje (DUA) y pedagogía STEAM',
    ]
  },
  {
    icon: '💻',
    title: 'Tecnología',
    puntos: [
      'Certificado de Profesionalidad en Confección y Publicación de Páginas Web y bootcamp de Data Science',
      'Python, Vue.js, Angular, SQL y Docker',
      'Esta web y su sistema de reservas, la web del Colegio Oficial de Geólogos del País Vasco y una plataforma multilingüe Erasmus+',
    ]
  }
]

const publicaciones = [
  {
    title: 'Environmental evolution of the Basque Coast Geopark estuaries (southern Bay of Biscay) during the last 10,000 years',
    fuente: 'Journal of Marine Systems, 2021',
    link: 'https://www.researchgate.net/publication/351054182_Environmental_evolution_of_the_Basque_Coast_Geopark_estuaries_southern_Bay_of_Biscay_during_the_last_10000_years',
  },
  {
    title: 'El registro geológico antropoceno del Basque Mud Patch y su relación con los procesos costeros en el Golfo de Bizkaia',
    fuente: 'SC Aranzadi / UPV-EHU / AEQUA, 2019',
    link: 'https://www.researchgate.net/publication/334785107_El_registro_geologico_antropoceno_del_Basque_Mud_Patch_y_su_relacion_con_los_procesos_costeros_en_el_Golfo_de_Bizkaia',
  },
  {
    title: 'Evolución ambiental de los estuarios del Geoparque de la Costa Vasca durante el Holoceno y el Antropoceno',
    fuente: 'SC Aranzadi / UPV-EHU / AEQUA, 2019',
    link: 'https://www.researchgate.net/publication/334785322_Evolucion_ambiental_de_los_estuarios_del_Geoparque_de_la_Costa_Vasca_durante_el_Holoceno_y_el_Antropoceno',
  },
  {
    title: 'Registro geológico del impacto humano en el estuario del Urola (Geoparque de la Costa Vasca) durante el Antropoceno',
    fuente: 'CKQ Estudios de Cuaternario, 2016',
    link: 'https://www.researchgate.net/publication/322117641_Registro_geologico_del_impacto_humano_en_el_estuario_del_Urola_Geoparque_de_la_Costa_Vasca_durante_el_Antropoceno',
  }
]

const goToDetail = (link) => {
  router.push(link)
}
</script>

<style scoped>
.general-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding-top: 7rem;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 4rem;
}

.intro-texto {
  font-size: 1.1rem;
  color: var(--darkgrey);
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 50rem;
}

.seccion {
  margin-bottom: 3.5rem;
}

.seccion > h2 {
  font-size: 1.5rem;
  color: var(--green);
  border-bottom: 2px solid var(--shoftgreen);
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
}

/* Proyectos: una sola rejilla, 4 por fila en escritorio */
.proyectos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
}

.card {
  padding: 1rem;
  background-color: var(--megashoftgreen);
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.card img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 0.5rem;
}

.card h3 {
  margin: 0.4rem 0 0.2rem;
  color: var(--darkgreen);
}

.card p {
  color: var(--darkgrey);
  font-size: 0.95rem;
  margin: 0.4rem 0 0;
}

.card .cliente {
  font-size: 0.85rem;
  font-weight: bold;
  color: var(--green);
  margin: 0;
}

.etiquetas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.75rem;
}

.etiqueta {
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  background-color: white;
  border: 1px solid var(--shoftgreen);
  color: var(--darkgreen);
}

/* Cifras */
.cifras {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 3rem;
}

.cifra {
  text-align: center;
  padding: 1.25rem 1rem;
  background-color: var(--megashoftgreen);
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
}

.cifra strong {
  display: block;
  font-size: 2.2rem;
  color: var(--darkgreen);
  line-height: 1.1;
}

.cifra span {
  font-size: 0.9rem;
  color: var(--darkgrey);
}

/* Colaboraciones */
.colaboraciones-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 1rem 2rem;
}

.colaboracion {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.colaboracion img {
  width: 110px;
  height: 110px;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 2px solid var(--shoftgreen);
  flex-shrink: 0;
}

.colaboracion h3 {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  color: var(--darkgreen);
}

.colaboracion p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--darkgrey);
  line-height: 1.5;
}

/* Quién hay detrás */
.persona {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.persona-foto {
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 50%;
  flex-shrink: 0;
}

.persona p {
  color: var(--darkgrey);
  line-height: 1.6;
  margin-top: 0;
}

.pilares-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.pilar {
  background-color: var(--megashoftgreen);
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
}

.pilar h3 {
  margin-top: 0;
  color: var(--green);
}

.pilar ul {
  padding-left: 1.1rem;
  margin: 0;
}

.pilar li {
  color: var(--darkgrey);
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.publicaciones h3 {
  color: var(--green);
  margin-bottom: 0.75rem;
}

.publicaciones ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.publicaciones li {
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--megashoftgreen);
}

.publicaciones a {
  color: var(--darkgreen);
  font-weight: bold;
  text-decoration: none;
}

.publicaciones a:hover {
  text-decoration: underline;
}

.pub-fuente {
  display: block;
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

/* Medios */
.medios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
}

.medio-card {
  background-color: #f9fdfb;
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.medio-card h3 {
  margin-top: 0;
  color: var(--green);
  font-size: 1.1rem;
}

.medio-card p {
  font-size: 0.9rem;
  color: var(--darkgrey);
  margin-bottom: 1rem;
}

/* Botones */
.btn-secundario {
  display: inline-block;
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.btn-secundario:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}

/* Cierre */
.cierre {
  text-align: center;
  background-color: var(--megashoftgreen);
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
  padding: 2rem;
}

.cierre h2 {
  margin-top: 0;
  color: var(--green);
}

.cierre p {
  color: var(--darkgrey);
}

.btn-principal {
  display: inline-block;
  background-color: var(--green);
  color: white;
  padding: 0.75rem 1.75rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.btn-principal:hover {
  background-color: var(--darkgreen);
}

@media (max-width: 613px) {
  .general-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .persona {
    flex-direction: column;
    text-align: center;
  }
}
</style>
